import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Edit2, Loader2, Search, Trash2, X } from 'lucide-react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import ErrorBoundary from './components/ErrorBoundary';
import { Header } from './components/Header';
import { BottomNav } from './components/BottomNav';
import { LoginScreen } from './components/LoginScreen';
import { CapacitorLoginScreen } from './components/CapacitorLoginScreen';
import { OverviewTab } from './components/OverviewTab';
import { ExpenditureTab } from './components/ExpenditureTab';
import { TransactionModal } from './components/modals/TransactionModal';
import { SettingsModal } from './components/modals/SettingsModal';
import { IncomeTab } from './components/IncomeTab';
import { SupportTab } from './components/SupportTab';
import { SavingsGoalsModal } from './components/modals/SavingsGoalsModal';
import { DebtModal } from './components/modals/DebtModal';
import { DebtPaymentModal } from './components/modals/DebtPaymentModal';
import { SubscriptionsModal } from './components/modals/SubscriptionsModal';
import { DeleteConfirmationModal } from './components/modals/DeleteConfirmationModal';
import { PaymentSuccessModal } from './components/modals/PaymentSuccessModal';
import {
  addDoc,
  auth,
  collection,
  db,
  deleteDoc,
  deleteObject,
  doc,
  isSignInWithEmailLink,
  signInWithEmailLink,
  getDocs,
  handleFirestoreError,
  limit,
  onAuthStateChanged,
  onSnapshot,
  OperationType,
  orderBy,
  query,
  ref,
  serverTimestamp,
  setDoc,
  storage,
  updateDoc,
  User,
  where,
} from './firebase';
import { FALLBACK_DATA } from './lib/constants';
import { AssetDetailScreen } from './components/AssetDetailScreen';
import { WealthTab } from './components/WealthTab';
import {
  Debt,
  SavingsGoal,
  Subscription,
  Transaction,
  Asset,
  AdminMessage,
} from './lib/types';
import { cn, formatCurrency } from './lib/utils';
import { exportTransactionsToCSV } from './lib/export';
import { NavigationDrawer } from './components/NavigationDrawer';
import { AboutModal } from './components/modals/AboutModal';
import {
  ContactSupportModal,
  SupportMessage,
} from './components/modals/ContactSupportModal';
import { PaywallScreen } from './components/PaywallScreen';
import { CapacitorPaywallScreen } from './components/CapacitorPaywallScreen';
import { BillingsModal } from './components/modals/BillingsModal';
import { BudgetModal, Budget } from './components/modals/BudgetModal';
import { BudgetTab } from './components/BudgetTab';
import { SubscriptionsTab } from './components/SubscriptionsTab';
import { SavingsTab } from './components/SavingsTab';
import { useAppSettings } from './hooks/useAppSettings';
import { PrivacyPolicyScreen } from './components/PrivacyPolicyScreen';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';

export default function App() {
  const { gtag } = useAppSettings();
  useGoogleAnalytics(gtag);

  return (
    <ErrorBoundary>
      <Kowope />
    </ErrorBoundary>
  );
}

function Kowope() {
  const [user, setUser] = useState<User | null>(null);
  const {
    expenseCategories,
    defaultIncomeEntries,
    defaultExpenditureEntries,
    currencies,
    graphColors,
  } = useAppSettings();
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'expenditure'
    | 'income'
    | 'debt'
    | 'support'
    | 'assets'
    | 'budget'
    | 'subscriptions'
    | 'savings'
    | 'asset-detail'
  >('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isBillingsModalOpen, setIsBillingsModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isContactSupportModalOpen, setIsContactSupportModalOpen] =
    useState(false);
  const [isSubscriptionsModalOpen, setIsSubscriptionsModalOpen] =
    useState(false);
  const [isDebtModalOpen, setIsDebtModalOpen] = useState(false);
  const [isDebtPaymentModalOpen, setIsDebtPaymentModalOpen] = useState(false);
  const [debtToPay, setDebtToPay] = useState<Debt | null>(null);
  const [isSavingsGoalsModalOpen, setIsSavingsGoalsModalOpen] = useState(false);
  const [editingSavingsGoal, setEditingSavingsGoal] =
    useState<SavingsGoal | null>(null);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [savingsGoals, setSavingsGoals] = useState<SavingsGoal[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([]);
  const [adminMessages, setAdminMessages] = useState<AdminMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<Transaction | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(
    null
  );
  const [debts, setDebts] = useState<Debt[]>([]);
  const [isAssetDeleteModalOpen, setIsAssetDeleteModalOpen] = useState(false);
  const [isDebtDeleteModalOpen, setIsDebtDeleteModalOpen] = useState(false);
  const [debtToDelete, setDebtToDelete] = useState<string | null>(null);
  const [assetToDelete, setAssetToDelete] = useState<string | null>(null);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [isSubscriptionDeleteModalOpen, setIsSubscriptionDeleteModalOpen] =
    useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<
    string | null
  >(null);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [isPrivacyMode, setIsPrivacyMode] = useState(false);
  const [hasPaid, setHasPaid] = useState<boolean | null>(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [prevHasPaid, setPrevHasPaid] = useState<boolean | null>(null);
  const [localCurrency, setLocalCurrency] = useState('USD');
  const [overseasCurrency, setOverseasCurrency] = useState('NGN');
  const [dateFilter, setDateFilter] = useState<
    'month' | '90days' | 'ytd' | 'lastYear' | 'all'
  >('month');

  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // Handle Email Link Sign-In Deep Links
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    CapApp.addListener('appUrlOpen', async (data: { url: string }) => {
      if (isSignInWithEmailLink(auth, data.url)) {
        let email = window.localStorage.getItem('emailForSignIn');

        // If the user opened the link on a different device/browser,
        // the email won't be in localStorage. We may need to prompt them.
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }

        if (email) {
          try {
            setLoading(true);
            await signInWithEmailLink(auth, email, data.url);
            window.localStorage.removeItem('emailForSignIn');
          } catch (error) {
            console.error('Error signing in with email link', error);
            alert('Failed to sign in. The link may have expired.');
          } finally {
            setLoading(false);
          }
        }
      }
    });

    return () => {
      CapApp.removeAllListeners();
    };
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = () => {
      root.classList.remove('light', 'dark');

      if (theme === 'system') {
        root.classList.add(mediaQuery.matches ? 'dark' : 'light');
      } else {
        root.classList.add(theme);
      }
    };

    applyTheme();

    mediaQuery.addEventListener('change', applyTheme);
    return () => mediaQuery.removeEventListener('change', applyTheme);
  }, [theme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsAuthReady(true);
      if (!user) {
        setTransactions([]);
        setLoading(false);
      } else {
        setLoading(true);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    const q = query(
      collection(db, 'transactions'),
      where('uid', '==', user.uid),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const txs = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Transaction
        );
        setTransactions(txs);
        setLoading(false);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'transactions');
      }
    );

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'settings'),
      where('uid', '==', user.uid),
      where('month', '==', selectedMonth),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const setting = snapshot.docs[0].data();
          setExchangeRate(setting.exchangeRate || 1);
        } else {
          setExchangeRate(1);
        }
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'settings');
      }
    );

    return () => unsubscribe();
  }, [user, selectedMonth]);

  // Fetch Subscriptions
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'subscriptions'),
      where('uid', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const subs = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Subscription
        );
        setSubscriptions(subs);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'subscriptions');
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Fetch Savings Goals
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'savings_goals'),
      where('uid', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const goals = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as SavingsGoal
        );
        setSavingsGoals(goals);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'savings_goals');
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Fetch Debts
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'debts'),
      where('uid', '==', user.uid),
      orderBy('currentBalance', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const userDebts = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Debt
        );
        setDebts(userDebts);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'debts');
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Fetch User Preferences (Global Settings)
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.theme) setTheme(data.theme);
        if (typeof data.privacyMode === 'boolean')
          setIsPrivacyMode(data.privacyMode);
        setHasPaid(!!data.hasPaid);
        if (data.localCurrency) setLocalCurrency(data.localCurrency);
        if (data.overseasCurrency) setOverseasCurrency(data.overseasCurrency);
      } else {
        setHasPaid(false);
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Detect payment success transition
  useEffect(() => {
    if (prevHasPaid === false && hasPaid === true) {
      setShowPaymentSuccess(true);
      const timer = setTimeout(() => setShowPaymentSuccess(false), 10000);
      return () => clearTimeout(timer);
    }
    setPrevHasPaid(hasPaid);
  }, [hasPaid]);

  // Fetch Assets
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'assets'), where('uid', '==', user.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const userAssets = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Asset
        );
        userAssets.sort((a, b) => b.value - a.value);
        setAssets(userAssets);
      },
      (error) => {
        console.error(
          'Error fetching assets (check Firestore rules/indexes):',
          error
        );
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Fetch Budgets
  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'budgets'), where('uid', '==', user.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const userBudgets = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Budget
        );
        setBudgets(userBudgets);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'budgets');
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Fetch Support Messages
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'support_messages'),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as SupportMessage
        );
        setSupportMessages(messages);
      },
      (error) => {
        console.log('Error fetching support messages', error);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Request Notification Permission
  useEffect(() => {
    if (
      user &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      Notification.requestPermission();
    }
  }, [user]);

  // Fetch Admin Messages
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'communications'),
      orderBy('createdAt', 'desc')
    );

    let isInitialLoad = true;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }) as AdminMessage)
          .filter((msg) => !msg.uid || msg.uid === user.uid);
        setAdminMessages(messages);

        if (!isInitialLoad) {
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const newMessage = change.doc.data() as AdminMessage;
              const isVisible = !newMessage.uid || newMessage.uid === user.uid;

              // Notify if the user hasn't read it yet
              if (isVisible && !newMessage.readBy?.includes(user.uid)) {
                if (Notification.permission === 'granted') {
                  new Notification('New Announcement', {
                    body:
                      newMessage.title || 'You have a new message from support',
                    icon: '/images/icons/pwa-192x192.webp',
                  });
                }
              }
            }
          });
        }
        isInitialLoad = false;
      },
      (error) => {
        console.log('Error fetching admin messages', error);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const hasUnreadMessages = useMemo(() => {
    if (!user) return false;
    return adminMessages.some((msg) => !msg.readBy?.includes(user.uid));
  }, [adminMessages, user]);

  const handleMarkAsRead = async (messageId: string) => {
    if (!user) return;
    const message = adminMessages.find((msg) => msg.id === messageId);
    if (message && !message.readBy?.includes(user.uid)) {
      const newReadBy = [...(message.readBy || []), user.uid];
      try {
        await updateDoc(doc(db, 'communications', messageId), {
          readBy: newReadBy,
        });
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  // Seed Default Transactions for the current month
  useEffect(() => {
    if (!user || loading || !isAuthReady) return;

    const seedDefaults = async () => {
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

      // Check if we already have ANY transactions for this month
      const monthTxs = transactions.filter((t) =>
        t.date.startsWith(currentMonth)
      );
      if (monthTxs.length > 0) return;

      // Double check with server to ensure no duplicates due to race conditions
      const q = query(
        collection(db, 'transactions'),
        where('uid', '==', user.uid),
        where('date', '>=', currentMonth),
        where('date', '<=', currentMonth + '\uf8ff'),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) return;

      console.log('Seeding default transactions for', currentMonth);

      const promises: Promise<unknown>[] = [];

      // Seed Support (Overseas Expenses)
      FALLBACK_DATA.gifts.forEach((gift) => {
        promises.push(
          addDoc(collection(db, 'transactions'), {
            type: 'expense',
            category: 'Overseas Expenses',
            amount: 0,
            date: `${currentMonth}-01`,
            note: gift.name,
            relationship: gift.relationship,
            allowance: 0,
            gift1: 0,
            gift2: 0,
            uid: user.uid,
          })
        );
      });

      // Seed Income
      defaultIncomeEntries.forEach((inc) => {
        promises.push(
          addDoc(collection(db, 'transactions'), {
            type: 'income',
            category: inc.category,
            amount: 0,
            date: `${currentMonth}-01`,
            note: inc.note,
            uid: user.uid,
          })
        );
      });

      // Seed Expenditure from Subscriptions
      // If user has no subscriptions defined, migrate defaults first
      let activeSubscriptions = subscriptions;
      if (subscriptions.length === 0 && defaultExpenditureEntries.length > 0) {
        const subPromises = defaultExpenditureEntries.map((def) =>
          addDoc(collection(db, 'subscriptions'), {
            category: def.category,
            note: def.note,
            amount: 0,
            uid: user.uid,
          })
        );
        await Promise.all(subPromises);
        // We don't need to wait for snapshot update, just use defaults for this run
        activeSubscriptions = defaultExpenditureEntries.map((d) => ({
          ...d,
          amount: 0,
          uid: user.uid,
          id: 'temp',
        }));
      }

      activeSubscriptions.forEach((sub) => {
        promises.push(
          addDoc(collection(db, 'transactions'), {
            type: 'expense',
            category: sub.category,
            amount: sub.amount,
            date: `${currentMonth}-01`,
            note: sub.note,
            uid: user.uid,
          })
        );
      });

      try {
        await Promise.all(promises);
      } catch (error) {
        handleFirestoreError(
          error,
          OperationType.CREATE,
          'transactions (seeding defaults)'
        );
      }
    };

    seedDefaults();
  }, [
    user,
    loading,
    transactions.length,
    subscriptions.length,
    isAuthReady,
    defaultIncomeEntries,
    defaultExpenditureEntries,
  ]);

  const handleUpdateExchangeRate = async (rate: number) => {
    if (!user) return;
    const q = query(
      collection(db, 'settings'),
      where('uid', '==', user.uid),
      where('month', '==', selectedMonth),
      limit(1)
    );

    try {
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        await updateDoc(doc(db, 'settings', snapshot.docs[0].id), {
          exchangeRate: rate,
        });
      } else {
        await addDoc(collection(db, 'settings'), {
          uid: user.uid,
          month: selectedMonth,
          exchangeRate: rate,
        });
      }
      setIsSettingsModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'settings');
    }
  };

  const processedTransactions = useMemo(() => {
    return transactions.map((t) => {
      if (t.category === 'Overseas Expenses') {
        const rate = exchangeRate || 1;
        return {
          ...t,
          nairaAmount: t.amount,
          amount: t.amount / rate,
          allowance: (t.allowance || 0) / rate,
          gift1: (t.gift1 || 0) / rate,
          gift2: (t.gift2 || 0) / rate,
        };
      }
      return t;
    });
  }, [transactions, exchangeRate]);

  // Filter transactions by date range for the dashboard views
  const displayedTransactions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();

    return processedTransactions.filter((t) => {
      if (dateFilter === 'month') {
        return t.date.startsWith(selectedMonth);
      }
      if (dateFilter === '90days') {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        return t.date >= d.toISOString().slice(0, 10);
      }
      if (dateFilter === 'ytd') {
        return t.date >= `${currentYear}-01-01`;
      }
      if (dateFilter === 'lastYear') {
        return (
          t.date >= `${currentYear - 1}-01-01` &&
          t.date <= `${currentYear - 1}-12-31`
        );
      }
      if (dateFilter === 'all') {
        return true;
      }
      return false;
    });
  }, [processedTransactions, selectedMonth, dateFilter]);

  const summary = useMemo(() => {
    const totalIncome = displayedTransactions
      .filter((t) => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = displayedTransactions
      .filter((t) => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    return {
      totalIncome,
      totalExpenses,
      balance: totalIncome - totalExpenses,
    };
  }, [displayedTransactions]);

  const categoryData = useMemo(() => {
    const categories: Record<string, { budget: number; actual: number }> = {};

    // Add actual expenses
    displayedTransactions
      .filter((t) => t.type === 'expense')
      .forEach((t) => {
        if (!categories[t.category])
          categories[t.category] = { budget: 0, actual: 0 };
        categories[t.category].actual += t.amount;
      });

    // Merge in defined budgets
    budgets.forEach((b) => {
      if (!categories[b.category]) {
        categories[b.category] = { budget: 0, actual: 0 };
      }
      categories[b.category].budget = b.amount;
    });

    return Object.entries(categories)
      .filter((data) => data[1].budget > 0 || data[1].actual > 0)
      .map(([name, data]) => ({
        name,
        budget: data.budget,
        actual: data.actual,
      }));
  }, [displayedTransactions, budgets]);

  const filteredTransactions = useMemo(() => {
    // This filter is for search, applied on top of month filter
    return displayedTransactions.filter(
      (t) =>
        t.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.note || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [displayedTransactions, searchTerm]);

  const groupedExpenditures = useMemo(() => {
    const expenses = filteredTransactions.filter((t) => t.type === 'expense');
    const supportItems = expenses.filter(
      (t) => t.category === 'Overseas Expenses'
    );
    const otherExpenses = expenses.filter(
      (t) => t.category !== 'Overseas Expenses'
    );

    // Deduplication logic: Group by note
    const expensesByNote: Record<string, Transaction[]> = {};
    otherExpenses.forEach((t) => {
      const key = (t.note || 'Untitled').trim().toLowerCase();
      if (!expensesByNote[key]) {
        expensesByNote[key] = [];
      }
      expensesByNote[key].push(t);
    });

    const uniqueOtherExpenses: Transaction[] = [];
    Object.values(expensesByNote).forEach((group) => {
      const nonZeroItems = group.filter((t) => t.amount > 0);
      if (nonZeroItems.length > 0) {
        uniqueOtherExpenses.push(...nonZeroItems);
      } else if (group.length > 0) {
        // If all are zero, keep only one (the first one)
        uniqueOtherExpenses.push(group[0]);
      }
    });

    // Calculate Naira totals for support
    const rawSupportItems = displayedTransactions.filter(
      (t) => t.category === 'Overseas Expenses'
    );
    const nairaTotals = rawSupportItems.reduce(
      (acc, item) => ({
        allowance: acc.allowance + (item.allowance || 0),
        gift1: acc.gift1 + (item.gift1 || 0),
        gift2: acc.gift2 + (item.gift2 || 0),
      }),
      { allowance: 0, gift1: 0, gift2: 0 }
    );

    const totals = supportItems.reduce(
      (acc, item) => ({
        allowance: acc.allowance + (item.allowance || 0),
        gift1: acc.gift1 + (item.gift1 || 0),
        gift2: acc.gift2 + (item.gift2 || 0),
      }),
      { allowance: 0, gift1: 0, gift2: 0 }
    );

    const summaryItems: Transaction[] = [
      {
        id: 'summary-allowance',
        note: 'Overseas Allowance Total',
        category: 'Support',
        amount: totals.allowance,
        nairaAmount: nairaTotals.allowance,
        date: 'Monthly Total',
        type: 'expense' as const,
        isSummary: true,
        uid: user?.uid || '',
      },
      {
        id: 'summary-gift1',
        note: 'Overseas Gift 1 Total',
        category: 'Support',
        amount: totals.gift1,
        nairaAmount: nairaTotals.gift1,
        date: 'Monthly Total',
        type: 'expense' as const,
        isSummary: true,
        uid: user?.uid || '',
      },
      {
        id: 'summary-gift2',
        note: 'Overseas Gift 2 Total',
        category: 'Support',
        amount: totals.gift2,
        nairaAmount: nairaTotals.gift2,
        date: 'Monthly Total',
        type: 'expense' as const,
        isSummary: true,
        uid: user?.uid || '',
      },
    ].filter((item) => item.amount > 0);

    const sortedOtherExpenses = uniqueOtherExpenses.sort((a, b) => {
      if (a.date !== b.date) {
        return b.date.localeCompare(a.date);
      }
      const noteA = a.note || '';
      const noteB = b.note || '';
      return noteA.localeCompare(noteB);
    });

    return [...summaryItems, ...sortedOtherExpenses];
  }, [filteredTransactions, user?.uid, displayedTransactions]);

  const groupedIncome = useMemo(() => {
    const income = filteredTransactions.filter((t) => t.type === 'income');

    // Group by note
    const incomeByNote: Record<string, Transaction> = {};
    income.forEach((t) => {
      const key = t.note || 'Untitled';
      if (!incomeByNote[key]) {
        incomeByNote[key] = t;
      } else {
        incomeByNote[key] = {
          ...incomeByNote[key],
          amount: incomeByNote[key].amount + t.amount,
        };
      }
    });

    return Object.values(incomeByNote);
  }, [filteredTransactions]);

  const supportRelationshipData = useMemo(() => {
    const supportItems = displayedTransactions.filter(
      (t) => t.category === 'Overseas Expenses'
    );
    const data: Record<string, number> = {};

    supportItems.forEach((item) => {
      const rel = item.relationship || 'OTHERS';
      data[rel] = (data[rel] || 0) + item.amount;
    });

    return Object.entries(data).map(([name, value]) => ({ name, value }));
  }, [displayedTransactions]);

  const supportDistributionData = useMemo(() => {
    const supportItems = displayedTransactions.filter(
      (t) => t.category === 'Overseas Expenses'
    );
    const totals = supportItems.reduce(
      (acc, item) => ({
        allowance: acc.allowance + (item.allowance || 0),
        gift1: acc.gift1 + (item.gift1 || 0),
        gift2: acc.gift2 + (item.gift2 || 0),
      }),
      { allowance: 0, gift1: 0, gift2: 0 }
    );

    return [
      { name: 'Allowance', value: totals.allowance },
      { name: 'Gift 1', value: totals.gift1 },
      { name: 'Gift 2', value: totals.gift2 },
    ].filter((item) => item.value > 0);
  }, [displayedTransactions]);

  const handleSaveTransaction = async (
    data: Omit<Transaction, 'id' | 'uid'>
  ) => {
    if (!user) return;

    const transactionData = {
      ...data,
      uid: user.uid,
      nairaAmount: 0, // Will be calculated if needed, or removed if not in schema
    };

    try {
      if (editingTransaction && !editingTransaction.id.startsWith('default-')) {
        await updateDoc(
          doc(db, 'transactions', editingTransaction.id),
          transactionData
        );
      } else {
        await addDoc(collection(db, 'transactions'), transactionData);

        // Automatically update savings goal if category is Savings
        if (data.category === 'Savings' && data.type === 'expense') {
          const goal = savingsGoals.find((g) => g.name === data.note);
          if (goal) {
            await handleUpdateSavingsGoal(goal.id, {
              currentAmount: goal.currentAmount + data.amount,
            });
          }
        }
      }
      setIsModalOpen(false);
      setEditingTransaction(null);
    } catch (error) {
      handleFirestoreError(
        error,
        editingTransaction ? OperationType.UPDATE : OperationType.CREATE,
        'transactions'
      );
    }
  };

  const handleDeleteTransaction = (id: string) => {
    setTransactionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleUpdateTheme = async (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    if (user) {
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          { theme: newTheme },
          { merge: true }
        );
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }
    }
  };

  const handleUpdatePrivacyMode = async (newMode: boolean) => {
    setIsPrivacyMode(newMode);
    if (user) {
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          { privacyMode: newMode },
          { merge: true }
        );
      } catch (error) {
        console.error('Error saving privacy preference:', error);
      }
    }
  };

  const handleUpdateLocalCurrency = async (currency: string) => {
    setLocalCurrency(currency);
    if (user) {
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          { localCurrency: currency },
          { merge: true }
        );
      } catch (error) {
        console.error('Error saving currency:', error);
      }
    }
  };

  const handleUpdateOverseasCurrency = async (currency: string) => {
    setOverseasCurrency(currency);
    if (user) {
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          { overseasCurrency: currency },
          { merge: true }
        );
      } catch (error) {
        console.error('Error saving currency:', error);
      }
    }
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;
    try {
      await deleteDoc(doc(db, 'transactions', transactionToDelete));
      setIsDeleteModalOpen(false);
      setTransactionToDelete(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'transactions');
    }
  };

  // Modal trigger state now just handles opening, the actual form data initialization
  // happens inside TransactionModal via useEffect
  const [defaultModalType, setDefaultModalType] = useState<
    'income' | 'expense'
  >('expense');
  const [defaultModalCategory, setDefaultModalCategory] = useState<string>('');

  const handleAddTransaction = (
    type: 'income' | 'expense',
    category?: string
  ) => {
    setEditingTransaction(null);
    setDefaultModalType(type);
    setDefaultModalCategory(category || '');
    setIsModalOpen(true);
  };

  const handleEditTransaction = (t: Transaction) => {
    const originalTransaction = transactions.find((tr) => tr.id === t.id) || t;
    setEditingTransaction(originalTransaction);
    setDefaultModalCategory(
      originalTransaction.category === 'Overseas Expenses'
        ? 'Overseas Expenses'
        : ''
    );
    setIsModalOpen(true);
  };

  const handleAddSubscription = async (
    sub: Omit<Subscription, 'id' | 'uid'>
  ) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'subscriptions'), { ...sub, uid: user.uid });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'subscriptions');
    }
  };

  const handleDeleteSubscription = (id: string) => {
    setSubscriptionToDelete(id);
    setIsSubscriptionDeleteModalOpen(true);
  };

  const confirmDeleteSubscription = async () => {
    if (subscriptionToDelete) {
      try {
        await deleteDoc(doc(db, 'subscriptions', subscriptionToDelete));
      } catch (error) {
        handleFirestoreError(error, OperationType.DELETE, 'subscriptions');
      }
      setIsSubscriptionDeleteModalOpen(false);
      setSubscriptionToDelete(null);
    }
  };

  const handleAddSavingsGoal = async (
    goal: Omit<SavingsGoal, 'id' | 'uid'>
  ) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'savings_goals'), { ...goal, uid: user.uid });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'savings_goals');
    }
  };

  const handleDeleteSavingsGoal = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'savings_goals', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'savings_goals');
    }
  };

  const handleAddBudget = async (budget: Omit<Budget, 'id' | 'uid'>) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'budgets'), { ...budget, uid: user.uid });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'budgets');
    }
  };

  const handleDeleteBudget = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'budgets', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'budgets');
    }
  };

  const handleExport = () => {
    exportTransactionsToCSV(
      displayedTransactions,
      `kowope-report-${dateFilter === 'month' ? selectedMonth : dateFilter}.csv`
    );
  };

  const handleUpdateSavingsGoal = async (
    id: string,
    data: Partial<SavingsGoal>
  ) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'savings_goals', id), data);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'savings_goals');
    }
  };

  const handleAddDebt = async (
    debtData: Omit<Debt, 'id' | 'uid' | 'currentBalance'> & {
      initialAmount: number;
    }
  ) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'debts'), {
        ...debtData,
        currentBalance: debtData.initialAmount,
        uid: user.uid,
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'debts');
    }
  };

  const handleDeleteDebt = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'debts', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'debts');
    }
  };

  const handleConfirmDeleteDebt = async () => {
    if (debtToDelete) {
      await handleDeleteDebt(debtToDelete);
      setIsDebtDeleteModalOpen(false);
      setDebtToDelete(null);
    }
  };

  const handleMakeDebtPayment = async (
    debtId: string,
    amount: number,
    date: string
  ) => {
    if (!user) return;
    const debt = debts.find((d) => d.id === debtId);
    if (!debt) return;

    const newBalance = debt.currentBalance - amount;

    try {
      // Record the payment as a transaction
      await addDoc(collection(db, 'transactions'), {
        type: 'expense',
        category: 'Debt Payment',
        amount,
        date,
        note: `Payment for ${debt.name}`,
        uid: user.uid,
      });
      // Update the debt balance
      await updateDoc(doc(db, 'debts', debtId), { currentBalance: newBalance });
      setIsDebtPaymentModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'debts/transactions');
    }
  };

  const handleAddAsset = async (assetData: Omit<Asset, 'id' | 'uid'>) => {
    if (!user) return;
    try {
      const docRef = await addDoc(collection(db, 'assets'), {
        ...assetData,
        uid: user.uid,
      });

      // Add initial history entry
      await addDoc(collection(db, 'assets', docRef.id, 'history'), {
        value: assetData.value,
        date: new Date().toISOString(),
        note: 'Initial value',
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'assets');
    }
  };

  const handleUpdateAsset = async (id: string, assetData: Partial<Asset>) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'assets', id), assetData);

      // If value changed, record history
      if (assetData.value !== undefined) {
        const previousAsset = assets.find((a) => a.id === id);
        if (previousAsset && previousAsset.value !== assetData.value) {
          await addDoc(collection(db, 'assets', id, 'history'), {
            value: assetData.value,
            date: new Date().toISOString(),
            note: 'Value update',
          });
        }
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'assets');
    }
  };

  const handleConfirmDeleteAsset = async () => {
    if (assetToDelete) {
      await handleDeleteAsset(assetToDelete);
      setIsAssetDeleteModalOpen(false);
      setAssetToDelete(null);
    }
  };

  const handleSendSupportMessage = async (subject: string, message: string) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'support_messages'), {
        uid: user.uid,
        email: user.email,
        name: user.displayName,
        subject,
        message,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'support_messages');
    }
  };

  const handleDeleteAsset = async (id: string) => {
    try {
      const assetToDelete = assets.find((a) => a.id === id);
      if (assetToDelete?.picture) {
        try {
          await deleteObject(ref(storage, assetToDelete.picture));
        } catch (error) {
          console.error('Error deleting asset image:', error);
        }
      }
      await deleteDoc(doc(db, 'assets', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'assets');
    }
  };

  if (!isAuthReady) return null;

  if (currentPath === '/privacy-policy') {
    return (
      <PrivacyPolicyScreen
        onBack={() => {
          window.history.pushState({}, '', '/');
          setCurrentPath('/');
        }}
      />
    );
  }

  if (!user) {
    return Capacitor.isNativePlatform() ? (
      <CapacitorLoginScreen />
    ) : (
      <LoginScreen />
    );
  }

  if (hasPaid === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!hasPaid) {
    return Capacitor.isNativePlatform() ? (
      <CapacitorPaywallScreen user={user} theme={theme} />
    ) : (
      <PaywallScreen user={user} theme={theme} />
    );
  }

  return (
    <div className="flex min-h-screen flex-col font-sans transition-colors duration-300">
      {/* Mobile Header */}
      <Header
        user={user}
        onSearchClick={() => setIsSearchOpen(true)}
        onMenuClick={() => setIsDrawerOpen(true)}
        onExportClick={handleExport}
        selectedMonth={selectedMonth}
        onMonthChange={(month) => {
          setSelectedMonth(month);
          setDateFilter('month');
        }}
        hasUnreadMessages={hasUnreadMessages}
      />

      <NavigationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveTab(tab);
          setIsDrawerOpen(false);
        }}
        onOpenSubscriptions={() => {
          setIsSubscriptionsModalOpen(true);
          setIsDrawerOpen(false);
        }}
        onOpenSettings={() => {
          setIsSettingsModalOpen(true);
          setIsDrawerOpen(false);
        }}
        onOpenBillings={() => {
          setIsBillingsModalOpen(true);
          setIsDrawerOpen(false);
        }}
        onOpenAbout={() => {
          setIsAboutModalOpen(true);
          setIsDrawerOpen(false);
        }}
        onOpenContactSupport={() => {
          setIsContactSupportModalOpen(true);
          setIsDrawerOpen(false);
        }}
        user={user}
        hasUnreadMessages={hasUnreadMessages}
      />

      <AnimatePresence>
        {isSearchOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[60] bg-zinc-50/95 p-6 backdrop-blur-xl dark:bg-zinc-950/95"
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="flex flex-1 items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-5 py-3 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                <Search className="h-5 w-5 text-zinc-400" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search transactions..."
                  className="w-full border-none bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-zinc-100 dark:placeholder:text-zinc-600"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchTerm('');
                }}
                className="rounded-2xl bg-zinc-900 p-3 text-white shadow-lg dark:bg-zinc-100 dark:text-zinc-900"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="max-h-[calc(100vh-120px)] space-y-4 overflow-y-auto">
              {searchTerm && (
                <p className="font-mono text-[10px] uppercase opacity-50">
                  Results for "{searchTerm}"
                </p>
              )}
              {searchTerm && filteredTransactions.length === 0 && (
                <p className="py-12 text-center italic opacity-40">
                  No results found
                </p>
              )}
              {filteredTransactions.map((t) => (
                <div
                  key={t.id}
                  className="group flex items-center justify-between rounded-3xl border border-zinc-100 bg-white p-5 shadow-sm transition-all active:scale-[0.98] dark:border-zinc-800 dark:bg-zinc-900"
                >
                  <div className="flex-1 space-y-1.5">
                    <p className="text-sm font-bold tracking-tight text-zinc-800 dark:text-zinc-200">
                      {t.note || 'Untitled'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase',
                          t.type === 'income'
                            ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
                        )}
                      >
                        {t.type}
                      </span>
                      <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[9px] font-bold tracking-wider text-zinc-500 uppercase dark:bg-zinc-800 dark:text-zinc-400">
                        {t.category}
                      </span>
                      <span className="text-[9px] font-bold text-zinc-400">
                        {t.date}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p
                        className={cn(
                          'text-base font-bold',
                          t.type === 'income'
                            ? 'text-emerald-600'
                            : 'text-zinc-900 dark:text-zinc-100'
                        )}
                      >
                        {t.type === 'income' ? '+' : '-'}
                        {formatCurrency(t.amount)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTransaction(t);
                          setIsSearchOpen(false);
                        }}
                        className="p-3 text-zinc-400 transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTransaction(t.id);
                        }}
                        className="p-3 text-zinc-400 transition-colors hover:text-rose-600 dark:hover:text-rose-400"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto px-6 pt-32 pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && (
              <OverviewTab
                exchangeRate={exchangeRate}
                localCurrency={localCurrency}
                transactions={transactions}
                setIsExchangeRateModalOpen={setIsSettingsModalOpen}
                summary={summary}
                categoryData={categoryData}
                assets={assets}
                debts={debts}
                supportRelationshipData={supportRelationshipData}
                supportDistributionData={supportDistributionData}
                selectedMonth={selectedMonth}
                savingsGoals={savingsGoals}
                onManageGoals={() => setActiveTab('savings')}
                onUpdateGoal={handleUpdateSavingsGoal}
                handleAddSavingsGoal={handleAddSavingsGoal}
                handleDeleteSavingsGoal={handleDeleteSavingsGoal}
                onExport={handleExport}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                isPrivacyMode={isPrivacyMode}
                graphColors={graphColors}
              />
            )}

            {activeTab === 'expenditure' && (
              <ExpenditureTab
                exchangeRate={exchangeRate}
                localCurrency={localCurrency}
                overseasCurrency={overseasCurrency}
                transactions={displayedTransactions}
                setIsExchangeRateModalOpen={setIsSettingsModalOpen}
                groupedExpenditures={groupedExpenditures}
                onAddTransaction={() => handleAddTransaction('expense')}
                onEditTransaction={handleEditTransaction}
                onDeleteTransaction={handleDeleteTransaction}
                onOpenSubscriptions={() => setIsSubscriptionsModalOpen(true)}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'income' && (
              <IncomeTab
                summary={summary}
                localCurrency={localCurrency}
                groupedIncome={groupedIncome}
                onAddTransaction={() => handleAddTransaction('income')}
                onEditTransaction={handleEditTransaction}
                onDeleteTransaction={handleDeleteTransaction}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'debt' && (
              <WealthTab
                initialView="debts"
                debts={debts}
                assets={assets}
                localCurrency={localCurrency}
                onManageDebts={() => setIsDebtModalOpen(true)}
                onMakePayment={(debt) => {
                  setDebtToPay(debt);
                  setIsDebtPaymentModalOpen(true);
                }}
                onManageAssets={() => {
                  setEditingAsset(null);
                  setActiveTab('asset-detail');
                }}
                onEditAsset={(asset) => {
                  setEditingAsset(asset);
                  setActiveTab('asset-detail');
                }}
                onDeleteAsset={(id) => {
                  setAssetToDelete(id);
                  setIsAssetDeleteModalOpen(true);
                }}
                onDeleteDebt={(id) => {
                  setDebtToDelete(id);
                  setIsDebtDeleteModalOpen(true);
                }}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'assets' && (
              <WealthTab
                initialView="assets"
                assets={assets}
                debts={debts}
                localCurrency={localCurrency}
                onManageDebts={() => setIsDebtModalOpen(true)}
                onMakePayment={(debt) => {
                  setDebtToPay(debt);
                  setIsDebtPaymentModalOpen(true);
                }}
                onManageAssets={() => {
                  setEditingAsset(null);
                  setActiveTab('asset-detail');
                }}
                onEditAsset={(asset) => {
                  setEditingAsset(asset);
                  setActiveTab('asset-detail');
                }}
                onDeleteAsset={(id) => {
                  setAssetToDelete(id);
                  setIsAssetDeleteModalOpen(true);
                }}
                onDeleteDebt={(id) => {
                  setDebtToDelete(id);
                  setIsDebtDeleteModalOpen(true);
                }}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'savings' && (
              <SavingsTab
                goals={savingsGoals}
                localCurrency={localCurrency}
                onManageGoals={() => {
                  setEditingSavingsGoal(null);
                  setIsSavingsGoalsModalOpen(true);
                }}
                onEditGoal={(goal) => {
                  setEditingSavingsGoal(goal);
                  setIsSavingsGoalsModalOpen(true);
                }}
                onDeleteGoal={handleDeleteSavingsGoal}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'support' && (
              <SupportTab
                transactions={displayedTransactions}
                searchTerm={searchTerm}
                exchangeRate={exchangeRate}
                localCurrency={localCurrency}
                overseasCurrency={overseasCurrency}
                onEditTransaction={handleEditTransaction}
                onDeleteTransaction={handleDeleteTransaction}
                onAddTransaction={() =>
                  handleAddTransaction('expense', 'Overseas Expenses')
                }
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'subscriptions' && (
              <SubscriptionsTab
                subscriptions={subscriptions}
                localCurrency={localCurrency}
                onAddSubscription={() => setIsSubscriptionsModalOpen(true)}
                onDeleteSubscription={handleDeleteSubscription}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'budget' && (
              <BudgetTab
                categoryData={categoryData}
                localCurrency={localCurrency}
                onManageBudgets={() => setIsBudgetModalOpen(true)}
                isPrivacyMode={isPrivacyMode}
              />
            )}

            {activeTab === 'asset-detail' && (
              <AssetDetailScreen
                asset={editingAsset}
                onBack={() => setActiveTab('assets')}
                onAddAsset={handleAddAsset}
                onUpdateAsset={handleUpdateAsset}
                currency={localCurrency}
                overseasCurrency={overseasCurrency}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingTransaction={editingTransaction}
        defaultType={defaultModalType}
        defaultCategory={defaultModalCategory}
        categories={expenseCategories}
        savingsGoals={savingsGoals}
        onSave={handleSaveTransaction}
      />

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        currentRate={exchangeRate}
        theme={theme}
        localCurrency={localCurrency}
        overseasCurrency={overseasCurrency}
        isPrivacyMode={isPrivacyMode}
        currencies={currencies}
        onUpdateTheme={handleUpdateTheme}
        onUpdateLocalCurrency={handleUpdateLocalCurrency}
        onUpdateOverseasCurrency={handleUpdateOverseasCurrency}
        onUpdatePrivacyMode={handleUpdatePrivacyMode}
        onUpdateRate={handleUpdateExchangeRate}
      />

      <BillingsModal
        isOpen={isBillingsModalOpen}
        onClose={() => setIsBillingsModalOpen(false)}
        user={user}
      />

      <AboutModal
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
      />

      <ContactSupportModal
        isOpen={isContactSupportModalOpen}
        onClose={() => setIsContactSupportModalOpen(false)}
        onSend={handleSendSupportMessage}
        supportMessages={supportMessages}
        adminMessages={adminMessages}
        currentUserId={user?.uid}
        onMarkAsRead={handleMarkAsRead}
      />

      <SubscriptionsModal
        isOpen={isSubscriptionsModalOpen}
        onClose={() => setIsSubscriptionsModalOpen(false)}
        onAddSubscription={handleAddSubscription}
        categories={expenseCategories}
      />

      <DebtModal
        isOpen={isDebtModalOpen}
        onClose={() => setIsDebtModalOpen(false)}
        onAddDebt={handleAddDebt}
      />

      <SavingsGoalsModal
        isOpen={isSavingsGoalsModalOpen}
        onClose={() => {
          setIsSavingsGoalsModalOpen(false);
          setEditingSavingsGoal(null);
        }}
        editingGoal={editingSavingsGoal}
        onAddGoal={handleAddSavingsGoal}
        onUpdateGoal={handleUpdateSavingsGoal}
        currency={localCurrency}
      />

      <BudgetModal
        isOpen={isBudgetModalOpen}
        onClose={() => setIsBudgetModalOpen(false)}
        budgets={budgets}
        onAddBudget={handleAddBudget}
        onDeleteBudget={handleDeleteBudget}
        categories={expenseCategories}
        currency={localCurrency}
      />

      <DebtPaymentModal
        isOpen={isDebtPaymentModalOpen}
        onClose={() => setIsDebtPaymentModalOpen(false)}
        debt={debtToPay}
        onConfirm={handleMakeDebtPayment}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        message="This action cannot be undone. This will permanently delete this transaction."
      />

      <DeleteConfirmationModal
        isOpen={isAssetDeleteModalOpen}
        onClose={() => setIsAssetDeleteModalOpen(false)}
        onConfirm={handleConfirmDeleteAsset}
        message="This action cannot be undone. This will permanently delete this asset."
      />

      <DeleteConfirmationModal
        isOpen={isSubscriptionDeleteModalOpen}
        onClose={() => setIsSubscriptionDeleteModalOpen(false)}
        onConfirm={confirmDeleteSubscription}
        message="This action cannot be undone. This will permanently delete this subscription."
      />

      <DeleteConfirmationModal
        isOpen={isDebtDeleteModalOpen}
        onClose={() => setIsDebtDeleteModalOpen(false)}
        onConfirm={handleConfirmDeleteDebt}
        message="This action cannot be undone. This will permanently delete this debt."
      />

      {/* Bottom Navigation */}
      <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />

      <PaymentSuccessModal
        isOpen={showPaymentSuccess}
        onClose={() => setShowPaymentSuccess(false)}
      />
    </div>
  );
}
